// =============================================================================
// TERM REPAIR — the transcription-repair pass for dictated / spoken answers
// (walk finding W22, second half: his dictated gap answer arrived as "multi
// Khloud environments… Cooper Berti's to track" — i.e. multi-cloud,
// Kubernetes — and he had to proofread it himself before pressing Send).
//
// WHAT THIS IS: a pure, data-driven repair pass that corrects a transcript
// ONLY toward terms that are already KNOWN — the curated technical lexicon
// below plus the user's OWN corpus vocabulary (served by the API from the
// frozen adjacent evidence on the gap-address session; see apps/api/lib/
// corpusTerms.ts). Every replacement string is READ OUT OF the vocabulary —
// there is no code path that synthesises a word.
//
// WHAT THIS IS NOT: a rewriter. It never rephrases, never expands, never
// "improves" the sentence, and never guesses at an unknown word. A word the
// vocabulary does not contain is left exactly as heard.
//
// NO INVENTION (the foundational law) — the code-enforced properties:
//   1. `replacement` is ALWAYS `entry.term` from the vocabulary. A term the
//      vocabulary does not carry can never be emitted (proven by test:
//      "every correction's replacement is a known vocabulary term").
//   2. Every correction is REPORTED (what was heard, what replaced it, why)
//      and REVERSIBLE (`revertCorrection`) — never a silent rewrite.
//   3. The user's own edit always wins: the report carries `original`, the
//      surface renders `corrected` into an editable field, and anything he
//      types over the top is what gets sent.
//
// SHAPE: deliberately mirrors apps/api/services/AuVernacularService.ts — a
// data library + a pure transformer + an audit list of what it actually did.
// That service is the closest existing sibling but NOT reusable here: its
// library is AU register (bet -> punt) applied to GENERATED prose under
// locale rule #4, which is the opposite job (it changes an author's wording;
// this only undoes a microphone's mistake). Growing this file means appending
// an entry with real observed provenance — no code changes elsewhere.
// =============================================================================

/** One term the system is allowed to correct TOWARD. */
export interface KnownTerm {
  /** The canonical spelling — the ONLY string this entry can ever emit. */
  term: string;
  /** Transcription mistakes actually OBSERVED for this term. Matched exactly
   *  (case-insensitive, whitespace- and apostrophe-flexible) before any fuzzy
   *  work, because a real observed mishearing is often far too distant for a
   *  distance metric to reach honestly ("Cooper Berti's" -> Kubernetes). */
  mishearings?: string[];
  /** Why the entry exists — provenance, surfaced verbatim in the audit line. */
  reason: string;
}

/**
 * The small explicit technical lexicon. Every entry earns its place with real
 * provenance; the `mishearings` lists carry only mistakes that have actually
 * been seen, never invented phonetic guesses.
 *
 * Deliberately SMALL. The broad, personal vocabulary comes from the user's own
 * corpus (served `corpusTerms`), not from this file growing into a dictionary.
 */
export const TECHNICAL_LEXICON: KnownTerm[] = [
  {
    term: 'multi-cloud',
    mishearings: ['multi khloud', 'multi cloud', 'multicloud', 'multi clowd'],
    reason: 'w22:observed — his dictated answer read "multi Khloud environments"',
  },
  {
    term: 'Kubernetes',
    mishearings: ["cooper berti's", 'cooper bertis', 'cooper berties', 'coopernetties', 'kubernetties'],
    reason: 'w22:observed — his dictated answer read "Cooper Berti\'s to track"',
  },
  {
    term: 'CommerceTools',
    mishearings: ['commerce tools', 'commerce tool'],
    reason: 'w22:named — a platform in his own corpus vocabulary',
  },
  { term: 'MuleSoft', mishearings: ['mule soft', 'mules oft'], reason: 'w22:named — integration platform' },
  { term: 'ServiceNow', mishearings: ['service now'], reason: 'stt:compound — spoken as two words' },
  { term: 'Salesforce', mishearings: ['sales force'], reason: 'stt:compound — spoken as two words' },
  { term: 'Cloudflare', mishearings: ['cloud flare'], reason: 'stt:compound — spoken as two words' },
  { term: 'Databricks', mishearings: ['data bricks'], reason: 'stt:compound — spoken as two words' },
  { term: 'Snowflake', mishearings: ['snow flake'], reason: 'stt:compound — spoken as two words' },
  { term: 'Terraform', mishearings: ['terra form'], reason: 'stt:compound — spoken as two words' },
  { term: 'GraphQL', mishearings: ['graph ql', 'graph q l'], reason: 'stt:compound — spelled-out suffix' },
  { term: 'PostgreSQL', mishearings: ['postgres ql', 'postgres q l'], reason: 'stt:compound — spelled-out suffix' },
  // Short/common-word terms carry NO mishearings and are excluded from the
  // fuzzy pass by MIN_FUZZY_TERM_CHARS below. They sit in the vocabulary so a
  // near-miss can never be "corrected" AWAY from them, not so they can be
  // corrected toward.
  { term: 'AWS', reason: 'w22:named — protected vocabulary' },
  { term: 'GCP', reason: 'w22:named — protected vocabulary' },
  { term: 'Epic', reason: 'w22:named — protected vocabulary' },
  { term: 'Azure', reason: 'w22:named — protected vocabulary' },
  { term: 'Kafka', reason: 'platform — protected vocabulary' },
];

/** Where a correction's authority came from. */
export type TermSource = 'lexicon' | 'corpus';

/** One correction actually performed — the audit row rendered to the user. */
export interface TermCorrection {
  /** Exactly what the transcript said, verbatim, before the swap. */
  heard: string;
  /** What replaced it. ALWAYS a term from the vocabulary — never synthesised. */
  replacement: string;
  /** Provenance for the audit line. */
  reason: string;
  /** Which vocabulary carried the term. */
  source: TermSource;
  /** Offset of `replacement` within `corrected`, for a stable audit ordering. */
  index: number;
}

export interface TermRepairReport {
  /** The transcript exactly as it arrived. Kept so the swap is reversible. */
  original: string;
  /** The transcript after every correction below. */
  corrected: string;
  /** Every substitution actually performed. Empty means nothing was touched. */
  corrections: TermCorrection[];
}

export interface TermRepairOptions {
  /** The user's OWN corpus vocabulary, served by the API (apps/api/lib/
   *  corpusTerms.ts mines it from the frozen adjacent evidence on this gap's
   *  session). Held to a STRICTER fuzzy threshold than the curated lexicon:
   *  mined terms are inferred, so they get less benefit of the doubt. */
  corpusTerms?: readonly string[];
}

/** Curated entries are hand-verified, so a slightly looser distance is safe. */
const LEXICON_FUZZY_THRESHOLD = 0.82;
/** Mined corpus terms are inferred from prose — hold them to a tighter bar. */
const CORPUS_FUZZY_THRESHOLD = 0.88;
/** Below this the distance metric is noise: 4-char words are all "close". */
const MIN_FUZZY_WINDOW_CHARS = 4;
/** Short terms (AWS, GCP, Epic) never take part in fuzzy matching at all. */
const MIN_FUZZY_TERM_CHARS = 5;
/** How many words a single term may span ("commerce tools" = 2). */
const MAX_WINDOW_WORDS = 3;

/** Comparison key: case-folded, punctuation- and space-free. */
function normalizeKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Levenshtein distance — small strings only, plain DP. */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = row;
  }
  return prev[b.length];
}

/** 1 = identical, 0 = nothing in common. */
export function similarity(a: string, b: string): number {
  const longest = Math.max(a.length, b.length);
  if (longest === 0) return 1;
  return 1 - levenshtein(a, b) / longest;
}

interface VocabEntry extends KnownTerm {
  source: TermSource;
  key: string;
  threshold: number;
}

/** Build the full vocabulary for a run: curated lexicon + served corpus terms.
 *  A corpus term that duplicates a lexicon term (case-insensitively) is
 *  dropped — the curated entry wins, keeping one authority per term. */
function buildVocabulary(opts: TermRepairOptions = {}): VocabEntry[] {
  const entries: VocabEntry[] = TECHNICAL_LEXICON.map((t) => ({
    ...t,
    source: 'lexicon' as const,
    key: normalizeKey(t.term),
    threshold: LEXICON_FUZZY_THRESHOLD,
  }));
  const seen = new Set(entries.map((e) => e.key));
  for (const raw of opts.corpusTerms ?? []) {
    const term = typeof raw === 'string' ? raw.trim() : '';
    if (!term) continue;
    const key = normalizeKey(term);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    entries.push({
      term,
      reason: 'corpus:your own words — this term appears in your evidence for this role',
      source: 'corpus',
      key,
      threshold: CORPUS_FUZZY_THRESHOLD,
    });
  }
  return entries;
}

/** The exact set of strings this pass is allowed to emit. Exported so the
 *  no-invention test can assert containment directly rather than trusting a
 *  code comment. */
export function knownVocabulary(opts: TermRepairOptions = {}): string[] {
  return buildVocabulary(opts).map((e) => e.term);
}

/** A mishearing may be written with a straight apostrophe but arrive with a
 *  curly one (and vice versa), and spoken word gaps vary — match both. */
function mishearingPattern(phrase: string): RegExp {
  const body = escapeRegExp(phrase).replace(/\s+/g, '\\s+').replace(/'/g, "['’]");
  return new RegExp(`(^|[^A-Za-z0-9'’-])(${body})(?![A-Za-z0-9-])`, 'gi');
}

/**
 * Repair a dictated / spoken transcript against KNOWN terms only.
 *
 * Pass 1 — observed mishearings: exact (case/whitespace/apostrophe-flexible)
 *          phrase matches from each entry's `mishearings` list.
 * Pass 2 — conservative fuzzy: 3-, 2- then 1-word windows compared to each
 *          vocabulary key, replaced only on a clear, unambiguous best match
 *          above the entry's threshold.
 *
 * A window already spelling a known term (modulo case) is left alone.
 */
export function repairTerms(text: string, opts: TermRepairOptions = {}): TermRepairReport {
  const original = typeof text === 'string' ? text : '';
  if (!original.trim()) return { original, corrected: original, corrections: [] };

  const vocab = buildVocabulary(opts);
  const corrections: Omit<TermCorrection, 'index'>[] = [];
  let corrected = original;

  // ── Pass 1: observed mishearings ─────────────────────────────────────────
  for (const entry of vocab) {
    for (const mishearing of entry.mishearings ?? []) {
      const pattern = mishearingPattern(mishearing);
      corrected = corrected.replace(pattern, (_match, lead: string, heard: string) => {
        // Already correct (only the case differs) — nothing to repair.
        if (heard.toLowerCase() === entry.term.toLowerCase()) return `${lead}${heard}`;
        corrections.push({
          heard,
          replacement: entry.term,
          reason: entry.reason,
          source: entry.source,
        });
        return `${lead}${entry.term}`;
      });
    }
  }

  // ── Pass 2: conservative fuzzy over word windows ──────────────────────────
  const tokens = [...corrected.matchAll(/[A-Za-z0-9][A-Za-z0-9'’.-]*/g)].map((m) => ({
    text: m[0],
    start: m.index ?? 0,
    end: (m.index ?? 0) + m[0].length,
  }));

  const fuzzyVocab = vocab.filter((e) => e.key.length >= MIN_FUZZY_TERM_CHARS);
  const replacements: { start: number; end: number; entry: VocabEntry; heard: string }[] = [];

  /** Longest window at `i` that ALREADY spells a known term (case aside), or 0.
   *  Checked before any fuzzy work: a correct term must be stepped over whole,
   *  never absorbed into a bigger window. Without this, "Kubernetes to track"
   *  fuzzy-matches "Kubernetes to" against Kubernetes and silently eats the
   *  "to" — a rewrite of his sentence, which this pass must never do. */
  function exactTermWindow(i: number): number {
    for (let n = Math.min(MAX_WINDOW_WORDS, tokens.length - i); n >= 1; n -= 1) {
      const heard = corrected.slice(tokens[i].start, tokens[i + n - 1].end).toLowerCase();
      if (vocab.some((e) => e.term.toLowerCase() === heard)) return n;
    }
    return 0;
  }

  let i = 0;
  while (i < tokens.length) {
    const exact = exactTermWindow(i);
    if (exact > 0) {
      i += exact;
      continue;
    }

    let advanced = false;
    for (let n = Math.min(MAX_WINDOW_WORDS, tokens.length - i); n >= 1 && !advanced; n -= 1) {
      const start = tokens[i].start;
      const end = tokens[i + n - 1].end;
      const heard = corrected.slice(start, end);
      const key = normalizeKey(heard);
      if (key.length < MIN_FUZZY_WINDOW_CHARS) continue;
      // A multi-word window that swallows an already-correct term is never a
      // candidate — same protection as exactTermWindow, for windows that start
      // one token earlier ("and Kubernetes", "ran multi-cloud").
      if (n > 1) {
        let swallowsCorrect = false;
        for (let k = i; k < i + n && !swallowsCorrect; k += 1) {
          if (exactTermWindow(k) > 0) swallowsCorrect = true;
        }
        if (swallowsCorrect) continue;
      }

      let best: VocabEntry | null = null;
      let bestScore = 0;
      let tied = false;
      for (const entry of fuzzyVocab) {
        const score = similarity(key, entry.key);
        if (score < entry.threshold) continue;
        if (score > bestScore) {
          bestScore = score;
          best = entry;
          tied = false;
        } else if (score === bestScore && best && entry.key !== best.key) {
          tied = true;
        }
      }

      if (best && !tied) {
        replacements.push({ start, end, entry: best, heard });
        i += n;
        advanced = true;
      }
    }
    if (!advanced) i += 1;
  }

  // Apply right-to-left so earlier offsets stay valid.
  for (const r of [...replacements].reverse()) {
    corrected = corrected.slice(0, r.start) + r.entry.term + corrected.slice(r.end);
  }
  for (const r of replacements) {
    corrections.push({
      heard: r.heard,
      replacement: r.entry.term,
      reason: r.entry.reason,
      source: r.entry.source,
    });
  }

  // Stamp each correction with where its replacement landed in the final text.
  const stamped: TermCorrection[] = corrections.map((c) => ({
    ...c,
    index: corrected.indexOf(c.replacement),
  }));
  stamped.sort((a, b) => a.index - b.index);

  return { original, corrected, corrections: stamped };
}

/**
 * Undo ONE correction in the (possibly further-edited) text: swap the first
 * occurrence of the replacement back to exactly what was heard. This is what
 * makes the pass reversible rather than a silent rewrite — the surface offers
 * it per audit row. Returns the text unchanged when the replacement is no
 * longer present (he already edited it himself; his text wins).
 */
export function revertCorrection(text: string, correction: TermCorrection): string {
  const at = text.indexOf(correction.replacement);
  if (at < 0) return text;
  return text.slice(0, at) + correction.heard + text.slice(at + correction.replacement.length);
}

/** One-line audit label: 'heard "multi Khloud" — used multi-cloud'. */
export function describeCorrection(c: TermCorrection): string {
  return `heard "${c.heard}" — used ${c.replacement}`;
}
